import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminAgentBookingHistory = () => {
  const { agentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [bookingHistory, setBookingHistory] = useState([]);
  const [agentInfo, setAgentInfo] = useState(null);
  const [filters, setFilters] = useState({
    period: 'ALL',
    status: 'ALL',
    search: ''
  });

  useEffect(() => {
    if (agentId) {
      fetchAgentBookingHistory();
    }
  }, [agentId]);

  const fetchAgentBookingHistory = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getAgentBookingHistory(agentId);
      if (response.success) {
        setBookingHistory(response.data);
        setAgentInfo({
          agentId: response.agentId,
          agentEmail: response.agentEmail,
          agentName: response.agentName,
          totalBookings: response.totalBookings
        });
      } else {
        toast.error(response.message || 'Failed to fetch agent booking history');
      }
    } catch (error) {
      console.error('Error fetching agent booking history:', error);
      toast.error('Failed to fetch agent booking history');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'BOOKED':
        return 'bg-green-100 text-green-800';
      case 'HOLD':
        return 'bg-yellow-100 text-yellow-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filterBookings = () => {
    return bookingHistory.filter(booking => {
      // Period filter
      if (filters.period !== 'ALL') {
        const months = parseInt(filters.period);
        const today = new Date();
        const checkInDate = new Date(booking.checkInDate);
        const diffMonths = (today.getFullYear() - checkInDate.getFullYear()) * 12 + 
                          (today.getMonth() - checkInDate.getMonth());
        if (diffMonths > months) return false;
      }

      // Status filter
      if (filters.status !== 'ALL' && booking.status !== filters.status) {
        return false;
      }

      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        return (
          booking.bookingId?.toLowerCase().includes(searchLower) ||
          booking.hotelName?.toLowerCase().includes(searchLower) ||
          booking.city?.toLowerCase().includes(searchLower) ||
          booking.provider?.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  };

  const filteredBookings = filterBookings();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Agent Booking History</h1>
            {agentInfo && (
              <p className="text-gray-600 mt-2">
                {agentInfo.agentName} ({agentInfo.agentEmail}) - {agentInfo.totalBookings} total bookings
              </p>
            )}
          </div>
          <Link 
            to="/admin/dashboard/agent-token-recharge" 
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            ← Back to Token Recharge
          </Link>
        </div>
      </div>

      {/* Agent Info Card */}
      {agentInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800">Agent ID</div>
              <div className="text-blue-900">{agentInfo.agentId}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-blue-800">Agent Email</div>
              <div className="text-blue-900">{agentInfo.agentEmail}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-blue-800">Total Bookings</div>
              <div className="text-blue-900 font-bold">{agentInfo.totalBookings}</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Booking Period</label>
            <select
              value={filters.period}
              onChange={(e) => handleFilterChange('period', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Time</option>
              <option value="3">Last 3 Months</option>
              <option value="6">Last 6 Months</option>
              <option value="9">Last 9 Months</option>
              <option value="12">Last 12 Months</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All</option>
              <option value="BOOKED">BOOKED</option>
              <option value="HOLD">HOLD</option>
              <option value="CANCELLED">CANCELLED</option>
              <option value="IN_PROGRESS">IN_PROGRESS</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search bookings..."
            />
          </div>
        </div>
      </div>

      {/* Booking History Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Booking History ({filteredBookings.length} bookings)
          </h2>
        </div>
        
        {filteredBookings && filteredBookings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hotel Booking ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hotel Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check-In</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check-Out</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Confirmation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBookings.map((booking, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link 
                        to={`/admin/dashboard/booking-details/${booking.bookingId}`}
                        className="text-blue-600 hover:text-blue-900 font-medium"
                      >
                        {booking.bookingId || 'N/A'}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.hotelBookingId || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.hotelName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.checkInDate || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.checkOutDate || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.city || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(booking.status)}`}>
                        {booking.status || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.provider || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.confirmationNumber || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {booking.totalPrice ? `₹${booking.totalPrice}` : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-8 text-center">
            <div className="text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No booking history found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filters.period !== 'ALL' || filters.status !== 'ALL' || filters.search
                  ? 'No bookings match the current filters.'
                  : 'This agent has no booking history.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Summary Statistics */}
      {filteredBookings.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{filteredBookings.length}</div>
              <div className="text-sm text-gray-600">Total Bookings</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {filteredBookings.filter(b => b.status === 'BOOKED').length}
              </div>
              <div className="text-sm text-gray-600">Confirmed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {filteredBookings.filter(b => b.status === 'CANCELLED').length}
              </div>
              <div className="text-sm text-gray-600">Cancelled</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                ₹{filteredBookings.reduce((sum, b) => sum + (parseFloat(b.totalPrice) || 0), 0).toFixed(2)}
              </div>
              <div className="text-sm text-gray-600">Total Value</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAgentBookingHistory;